import { auth, db } from '../../config/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export interface AuthState {
  user: User | null;
  isPremium: boolean;
  subscriptionStatus: string;
  roles: string[];
  loading: boolean;
  error: string | null;
}

type Listener = (state: AuthState) => void;

export class AuthStore {
  private state: AuthState = {
    user: null,
    isPremium: false,
    subscriptionStatus: 'free',
    roles: [],
    loading: true,
    error: null,
  };

  private listeners: Set<Listener> = new Set();

  /** Incremented on every auth state change so a stale loadUserData response
   * from a previous user can never overwrite the current user's roles —
   * closes a privilege-leak race between rapid sign-out/sign-in cycles. */
  private authGeneration = 0;

  constructor() {
    onAuthStateChanged(auth, async (user) => {
      const generation = ++this.authGeneration;

      this.state.user = user;
      this.state.error = null;

      if (user) {
        await this.loadUserData(user.uid, generation);
      } else {
        this.state.isPremium = false;
        this.state.subscriptionStatus = 'free';
        this.state.roles = [];
      }

      // Only the latest auth event is allowed to clear loading/notify.
      if (generation === this.authGeneration) {
        this.state.loading = false;
        this.notify();
      }
    });
  }

  private async loadUserData(uid: string, generation: number): Promise<void> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));

      // Discard if superseded by a newer auth state change while awaiting.
      if (generation !== this.authGeneration) return;

      if (userDoc.exists()) {
        const data = userDoc.data();
        this.state.isPremium = data.subscriptionStatus === 'active';
        this.state.subscriptionStatus =
          typeof data.subscriptionStatus === 'string' ? data.subscriptionStatus : 'free';
        this.state.roles = Array.isArray(data.roles) ? data.roles : ['user'];
      } else {
        this.state.roles = ['user'];
      }
    } catch (err) {
      if (generation !== this.authGeneration) return;

      console.warn('Failed to load user data:', err);
      this.state.error =
        err instanceof Error ? err.message : 'Failed to load account permissions';
      // Fail closed: no elevated access if role data couldn't be verified.
      this.state.isPremium = false;
      this.state.subscriptionStatus = 'free';
      this.state.roles = [];
    }
  }

  get user(): User | null {
    return this.state.user;
  }

  get isAuthenticated(): boolean {
    return !!this.state.user && !this.state.loading;
  }

  get isPremium(): boolean {
    return this.state.isPremium;
  }

  get isAdmin(): boolean {
    return this.state.roles.includes('admin');
  }

  get isModerator(): boolean {
    return this.state.roles.includes('moderator') || this.isAdmin;
  }

  get loading(): boolean {
    return this.state.loading;
  }

  get error(): string | null {
    return this.state.error;
  }

  /** Re-fetches role/subscription data for the current user without requiring
   * a sign-out/sign-in cycle — useful right after an admin promotion/demotion. */
  async refresh(): Promise<void> {
    const user = this.state.user;
    if (!user) return;

    const generation = ++this.authGeneration;
    this.state.loading = true;
    this.state.error = null;
    this.notify();

    await this.loadUserData(user.uid, generation);

    if (generation === this.authGeneration) {
      this.state.loading = false;
      this.notify();
    }
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener({ ...this.state });
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const snapshot = { ...this.state };
    this.listeners.forEach((fn) => fn(snapshot));
  }
}

export const authStore = new AuthStore();
